import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import './App.css';

const WHATSAPP = '919177689562';
const WHATSAPP_DISPLAY = '+91 91776 89562';
const PHONE = '+919121286043';
const CONTACT_EMAIL = 'careercampus.tech1@gmail.com';

const COURSES = [
  {
    title: 'AI & ML',
    category: 'AI',
    duration: '6 Months',
    image:
      'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1200&q=80',
    details:
      'Learn machine learning fundamentals, model building, MLOps workflow, and portfolio capstone delivery.',
  },
  {
    title: 'Python With AI',
    category: 'Programming',
    duration: '4 Months',
    image:
      'https://images.unsplash.com/photo-1526379095098-d400fd0bf935?auto=format&fit=crop&w=1200&q=80',
    details:
      'Master Python, automation, prompt engineering, and AI-enabled application development.',
  },
  {
    title: 'Data Science with AI',
    category: 'Data',
    duration: '6 Months',
    image:
      'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80',
    details:
      'From statistics to predictive modeling, build data-driven products using AI-first workflows.',
  },
  {
    title: 'Data Analytics with AI',
    category: 'Data',
    duration: '4 Months',
    image:
      'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1200&q=80',
    details:
      'Learn dashboarding, SQL, Excel, BI tools, and AI-assisted analytics for decision-making.',
  },
  {
    title: 'Full Stack Developer (Java, Python)',
    category: 'Development',
    duration: '7 Months',
    image:
      'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?auto=format&fit=crop&w=1200&q=80',
    details:
      'Build complete web apps with backend APIs, frontend UI, databases, authentication, and deployment.',
  },
  {
    title: '.NET Developer',
    category: 'Development',
    duration: '5 Months',
    image:
      'https://images.unsplash.com/photo-1518773553398-650c184e0bb3?auto=format&fit=crop&w=1200&q=80',
    details:
      'C#, ASP.NET Core, enterprise APIs, clean architecture, and cloud-ready project delivery.',
  },
  {
    title: 'Cyber Security with AI',
    category: 'Security',
    duration: '5 Months',
    image:
      'https://images.unsplash.com/photo-1563013544-824ae1b704d3?auto=format&fit=crop&w=1200&q=80',
    details:
      'Threat detection, SOC workflows, SIEM concepts, and AI-supported security operations.',
  },
  {
    title: 'Cloud Computing (AWS, Azure, GCP) with DevOps & AI',
    category: 'Cloud',
    duration: '6 Months',
    image:
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80',
    details:
      'Multi-cloud architecture, CI/CD, containers, Kubernetes, and AI-assisted infrastructure automation.',
  },
  {
    title: 'UI/UX Design',
    category: 'Design',
    duration: '4 Months',
    image:
      'https://images.unsplash.com/photo-1545235617-9465d2a55698?auto=format&fit=crop&w=1200&q=80',
    details:
      'User research, wireframes, prototyping, design systems, and product design portfolio creation.',
  },
  {
    title: 'Digital Marketing',
    category: 'Marketing',
    duration: '3 Months',
    image:
      'https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=1200',
    details:
      'SEO, ads, social growth, funnel strategy, analytics, and campaign optimization at scale.',
  },
  {
    title: 'Medical Coding with AI (CPC, CCS), IP-DRG, Surgery, E/M, OP, HCC',
    category: 'Healthcare',
    duration: '6 Months',
    image:
      'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80',
    details:
      'Medical coding standards, compliance, coding audits, and AI-assisted documentation workflows.',
  },
  {
    title: 'Medical Billing & RCM with AI',
    category: 'Healthcare',
    duration: '4 Months',
    image:
      'https://images.pexels.com/photos/8376277/pexels-photo-8376277.jpeg?auto=compress&cs=tinysrgb&w=1200',
    details:
      'Claims lifecycle, denial management, RCM optimization, and process automation with AI tools.',
  },
];

const COURSE_READ_MORE = {
  'AI & ML': {
    curriculum: ['Supervised and unsupervised ML', 'Model evaluation and tuning', 'Mini capstone on real dataset'],
    tools: ['Python', 'scikit-learn', 'Jupyter', 'Git'],
    roles: ['ML Engineer Intern', 'AI Analyst', 'Data Science Associate'],
  },
  'Python With AI': {
    curriculum: ['Python core to advanced', 'Automation scripts', 'AI app integration patterns'],
    tools: ['Python', 'FastAPI', 'Pandas', 'OpenAI APIs'],
    roles: ['Python Developer', 'Automation Engineer', 'AI App Developer'],
  },
  'Data Science with AI': {
    curriculum: ['Statistics and EDA', 'Predictive modeling', 'Business problem solving'],
    tools: ['Pandas', 'NumPy', 'scikit-learn', 'Power BI'],
    roles: ['Data Scientist', 'ML Analyst', 'Decision Intelligence Associate'],
  },
  'Data Analytics with AI': {
    curriculum: ['Excel to SQL analytics', 'Dashboard storytelling', 'AI-augmented data insights'],
    tools: ['Excel', 'SQL', 'Power BI', 'Tableau'],
    roles: ['Data Analyst', 'BI Analyst', 'Reporting Specialist'],
  },
  'Full Stack Developer (Java, Python)': {
    curriculum: ['Backend APIs + frontend UI', 'Authentication and databases', 'Deploy full-stack project'],
    tools: ['Spring Boot', 'React', 'Node tools', 'MySQL'],
    roles: ['Full Stack Developer', 'Backend Developer', 'Software Engineer'],
  },
  '.NET Developer': {
    curriculum: ['C# fundamentals', 'ASP.NET Core APIs', 'Enterprise coding standards'],
    tools: ['C#', '.NET Core', 'SQL Server', 'Azure basics'],
    roles: ['.NET Developer', 'API Developer', 'Software Engineer'],
  },
  'Cyber Security with AI': {
    curriculum: ['Network security basics', 'Threat detection workflow', 'AI-assisted SOC analysis'],
    tools: ['SIEM basics', 'Security labs', 'Python security scripts'],
    roles: ['SOC Analyst', 'Security Analyst', 'Cyber Ops Associate'],
  },
  'Cloud Computing (AWS, Azure, GCP) with DevOps & AI': {
    curriculum: ['Cloud foundations multi-platform', 'CI/CD and containerization', 'Monitoring and reliability'],
    tools: ['AWS', 'Azure', 'GCP', 'Docker/Kubernetes'],
    roles: ['Cloud Engineer', 'DevOps Engineer', 'Site Reliability Associate'],
  },
  'UI/UX Design': {
    curriculum: ['UX research and wireframes', 'High-fidelity UI systems', 'Portfolio case study creation'],
    tools: ['Figma', 'Adobe XD', 'Miro', 'Prototyping kits'],
    roles: ['UI Designer', 'UX Designer', 'Product Design Associate'],
  },
  'Digital Marketing': {
    curriculum: ['SEO and performance marketing', 'Social and content strategy', 'Campaign analytics and optimization'],
    tools: ['Google Ads', 'Meta Ads', 'GA4', 'SEO tools'],
    roles: ['Digital Marketing Executive', 'Performance Marketer', 'SEO Analyst'],
  },
  'Medical Coding with AI (CPC, CCS), IP-DRG, Surgery, E/M, OP, HCC': {
    curriculum: ['CPC and CCS coding standards', 'IP-DRG and OP coding workflows', 'Compliance and audit readiness'],
    tools: ['Coding manuals', 'Claim tools', 'Documentation checklists'],
    roles: ['Medical Coder', 'Coding Auditor', 'Healthcare Documentation Specialist'],
  },
  'Medical Billing & RCM with AI': {
    curriculum: ['End-to-end claims lifecycle', 'Denial management and AR follow-up', 'RCM KPI optimization'],
    tools: ['RCM platforms', 'Billing workflow sheets', 'Reporting templates'],
    roles: ['RCM Executive', 'Medical Billing Specialist', 'Claims Analyst'],
  },
};

const COURSE_CARD_TITLES = {
  'AI & ML': 'AI & ML Career Track',
  'Python With AI': 'Python + AI Developer Track',
  'Data Science with AI': 'Data Science Specialist Track',
  'Data Analytics with AI': 'Data Analytics Pro Track',
  'Full Stack Developer (Java, Python)': 'Full Stack Engineering Track',
  '.NET Developer': '.NET Enterprise Developer Track',
  'Cyber Security with AI': 'Cyber Security Defender Track',
  'Cloud Computing (AWS, Azure, GCP) with DevOps & AI': 'Cloud DevOps Architect Track',
  'UI/UX Design': 'UI/UX Product Design Track',
  'Digital Marketing': 'Growth Marketing Track',
  'Medical Coding with AI (CPC, CCS), IP-DRG, Surgery, E/M, OP, HCC': 'Medical Coding Expert Track',
  'Medical Billing & RCM with AI': 'Medical Billing & RCM Track',
};

const COURSE_CARD_SUBTITLES = {
  'AI & ML': 'Model building, MLOps, and AI deployment',
  'Python With AI': 'Automation, APIs, and AI integrations',
  'Data Science with AI': 'Statistics, ML, and business outcomes',
  'Data Analytics with AI': 'Dashboards, BI, and insight storytelling',
  'Full Stack Developer (Java, Python)': 'Frontend + backend + database mastery',
  '.NET Developer': 'C#, ASP.NET Core, and enterprise APIs',
  'Cyber Security with AI': 'Threat detection and SOC readiness',
  'Cloud Computing (AWS, Azure, GCP) with DevOps & AI': 'Cloud + CI/CD + container orchestration',
  'UI/UX Design': 'Research, wireframing, and design systems',
  'Digital Marketing': 'SEO, paid ads, and campaign analytics',
  'Medical Coding with AI (CPC, CCS), IP-DRG, Surgery, E/M, OP, HCC': 'Coding standards and compliance excellence',
  'Medical Billing & RCM with AI': 'Claims lifecycle and denial management',
};

const COURSE_SYLLABUS = Object.fromEntries(
  COURSES.map((course) => {
    const info = COURSE_READ_MORE[course.title] || { curriculum: [], tools: [], roles: [] };
    const modules = [
      {
        title: 'Module 1: Core Topic Foundation',
        topic: `${course.title} fundamentals and structured concept clarity`,
        subTopics: info.curriculum.map((subTopic) => ({
          name: subTopic,
          details: `Build strong understanding of ${subTopic.toLowerCase()} with guided examples, mentor explanation, and practice checkpoints.`,
        })),
      },
      {
        title: 'Module 2: Practical Tools and Implementation',
        topic: `Hands-on implementation using ${course.title} tools and workflows`,
        subTopics: [
          ...info.tools.map((subTopic) => ({
            name: subTopic,
            details: `Practice ${subTopic} through lab-style activities, real use cases, and step-by-step execution support.`,
          })),
          {
            name: 'Case-study based assignments',
            details:
              'Apply concepts in structured case studies so students can confidently explain their approach during interviews.',
          },
          {
            name: 'Guided mini-project tasks',
            details:
              'Complete mini-project tasks with feedback to improve practical confidence and implementation quality.',
          },
        ],
      },
      {
        title: 'Module 3: Career Readiness and Portfolio Delivery',
        topic: `${course.title} interview preparation, profile building, and role-aligned practice`,
        subTopics: [
          {
            name: 'Project documentation and presentation',
            details:
              'Learn how to present project problem statement, solution flow, outcomes, and personal contribution clearly.',
          },
          {
            name: 'Mock interview preparation',
            details:
              'Practice technical and HR interview scenarios with revision pointers and improvement feedback.',
          },
          ...info.roles.map((role) => ({
            name: `${role} role-focused preparation`,
            details: `Get role-specific preparation strategy for ${role}, including required skill mapping and interview question focus.`,
          })),
        ],
      },
    ];

    return [
      course.title,
      {
        intro: `This syllabus is designed for learners who want a practical, job-focused learning path in ${course.title}.`,
        whoShouldJoin: [
          'Students who want clear topic-by-topic learning with practical guidance.',
          'Graduates and freshers preparing for interviews and placement opportunities.',
          'Working professionals planning to switch into a stronger role.',
        ],
        modules,
        joinUs:
          'Join Career Campus to learn from mentors, build project confidence, and move toward interview-ready career outcomes.',
      },
    ];
  })
);

const imageDataUrlCache = new Map();
const toDataUrl = async (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image'));
    reader.readAsDataURL(blob);
  });

const loadImageDataUrl = async (url) => {
  if (imageDataUrlCache.has(url)) return imageDataUrlCache.get(url);
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Image not found: ${url}`);
  const blob = await response.blob();
  const dataUrl = await toDataUrl(blob);
  imageDataUrlCache.set(url, dataUrl);
  return dataUrl;
};

const downloadSyllabusPdf = async (course) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 48;
  const right = 48;
  const maxWidth = pageWidth - left - right;
  let y = 70;

  let logoDataUrl = null;
  let qrDataUrl = null;
  const waLink = `https://wa.me/${WHATSAPP}`;

  const drawFooter = () => {
    const footerY = pageHeight - 30;
    doc.setDrawColor(203, 213, 225);
    doc.line(left, footerY - 10, pageWidth - right, footerY - 10);

    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', left, footerY - 12, 14, 14);
    }
    doc.setTextColor(71, 85, 105);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('Career Campus | +91 91212 86043 | careercampus.tech1@gmail.com', left + 18, footerY - 2);
    doc.text('Scan QR to ask fee on WhatsApp', pageWidth - right - 165, footerY - 2);
    if (qrDataUrl) {
      doc.addImage(qrDataUrl, 'PNG', pageWidth - right - 36, footerY - 30, 28, 28);
    }
  };

  const ensureSpace = (needed = 20) => {
    if (y + needed <= pageHeight - 48) return;
    drawFooter();
    doc.addPage();
    y = 60;
  };

  const writeLine = (text, size = 11, spacing = 18) => {
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line) => {
      ensureSpace(spacing);
      doc.text(line, left, y);
      y += spacing;
    });
  };

  const syllabus = COURSE_SYLLABUS[course.title];
  if (!syllabus) return;

  try {
    logoDataUrl = await loadImageDataUrl('/career-campus-logo.png');
    qrDataUrl = await QRCode.toDataURL(waLink, {
      width: 220,
      margin: 1,
      color: {
        dark: '#0f172a',
        light: '#ffffff',
      },
    });
  } catch (_error) {
    // Keep PDF generation robust even if image loading fails.
  }

  try {
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, 'PNG', left, 42, 56, 56);
      y = 112;
    }
  } catch (_error) {
    // Keep PDF generation robust even if logo loading fails.
  }

  doc.setFillColor(30, 64, 175);
  doc.roundedRect(left, y - 26, maxWidth, 30, 6, 6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  writeLine('Career Campus - Detailed Syllabus', 16, 22);
  doc.setTextColor(15, 23, 42);
  writeLine(course.title, 14, 20);
  y += 4;

  doc.setFont('helvetica', 'normal');
  writeLine(syllabus.intro, 11, 16);
  y += 6;

  doc.setFont('helvetica', 'bold');
  writeLine('Who Should Join', 12, 18);
  doc.setFont('helvetica', 'normal');
  syllabus.whoShouldJoin.forEach((point) => writeLine(`- ${point}`, 11, 16));
  y += 8;

  doc.setFont('helvetica', 'bold');
  writeLine('Module-Wise Syllabus', 12, 18);
  y += 2;

  syllabus.modules.forEach((module, moduleIndex) => {
    ensureSpace(58);
    doc.setFillColor(239, 246, 255);
    doc.setDrawColor(147, 197, 253);
    doc.roundedRect(left, y - 12, maxWidth, 26, 4, 4, 'FD');
    doc.setFont('helvetica', 'bold');
    writeLine(`${module.title}`, 12, 18);
    doc.setTextColor(30, 64, 175);
    writeLine(`Topic: ${module.topic}`, 11, 16);
    doc.setTextColor(15, 23, 42);
    doc.setFont('helvetica', 'normal');
    module.subTopics.forEach((subTopic, subIndex) => {
      writeLine(`Sub-topic ${subIndex + 1}: ${subTopic.name}`, 11, 16);
      writeLine(`Details: ${subTopic.details}`, 10.5, 15);
      y += 2;
    });
    if (moduleIndex < syllabus.modules.length - 1) y += 6;
  });

  doc.setFont('helvetica', 'bold');
  writeLine('Why Learn With Career Campus', 12, 18);
  doc.setFont('helvetica', 'normal');
  writeLine(
    'You get mentor-led training, practical assignments, interview preparation, and placement-focused guidance in every track.',
    11,
    16
  );
  y += 4;
  writeLine(syllabus.joinUs, 11, 16);
  y += 8;
  doc.setFont('helvetica', 'bold');
  writeLine(`To enroll, contact us at ${CONTACT_EMAIL} or call +91 91212 86043.`, 11, 16);
  drawFooter();

  const safeName = course.title.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  doc.save(`${safeName}-syllabus.pdf`);
};

const REVIEWS = [
  {
    name: 'Akhila R.',
    role: 'Data Analyst',
    image:
      'https://images.pexels.com/photos/762020/pexels-photo-762020.jpeg?auto=compress&cs=tinysrgb&w=600',
    quote:
      'Career Campus gave me practical projects, interview mentorship, and personal guidance that improved my confidence and profile. I received better interview opportunities and a clear career direction because of this institution.',
  },
  {
    name: 'Madhan K.',
    role: 'Python Developer',
    image:
      'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=600',
    quote:
      'The faculty support at Career Campus is exceptional. Their live coding sessions, placement training, and mock interviews helped me crack multiple interviews and move into a stronger role.',
  },
  {
    name: 'Sreelatha P.',
    role: 'Medical Coding Specialist',
    image:
      'https://images.pexels.com/photos/1181690/pexels-photo-1181690.jpeg?auto=compress&cs=tinysrgb&w=600',
    quote:
      'I am very grateful to Career Campus for the way they trained me in medical coding and billing. Their structured teaching and career support helped me enhance my skills and secure better career growth.',
  },
];

const SERVICE_HIGHLIGHTS = [
  {
    title: 'Industry Mentorship',
    text: 'Learn directly from experienced professionals with practical production insights and interview guidance.',
    more: 'Students receive one-on-one mentoring, career roadmap reviews, and role-based interview coaching from experienced industry professionals.',
  },
  {
    title: 'Live Project Labs',
    text: 'Every track includes project-based implementation to build confidence and portfolio credibility.',
    more: 'Learners build real projects end-to-end with code reviews, deployment guidance, and portfolio polishing aligned to hiring expectations.',
  },
  {
    title: 'Placement Readiness',
    text: 'Structured support with resume optimization, mock interviews, aptitude rounds, and HR preparation.',
    more: 'Comprehensive placement support covers resume building, communication training, mock rounds, and interview strategy sessions.',
  },
  {
    title: 'Flexible Learning Modes',
    text: 'Online, offline, and hybrid options for students, graduates, and working professionals.',
    more: 'Students can switch between online and classroom learning, making it easy to stay consistent without disrupting work or studies.',
  },
  {
    title: 'Career Counselling',
    text: 'Personalized roadmap planning based on your background, goals, and target job role.',
    more: 'Dedicated counsellors help choose the right track, define milestones, and guide progress from learning phase to job placement.',
  },
  {
    title: 'Continuous Assessment',
    text: 'Weekly evaluations, feedback loops, and improvement plans to track consistent progress.',
    more: 'Regular assessments, performance tracking, and mentor feedback ensure measurable improvement throughout the training journey.',
  },
];

const BLOGS = [
  {
    title: 'How to Build an AI Career Roadmap in 120 Days',
    image:
      'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1200&q=80',
    excerpt:
      'A practical week-by-week roadmap from beginner to interview-ready professional.',
  },
  {
    title: 'Top Hiring Trends in Cloud, Data, and Cyber Security',
    image:
      'https://images.unsplash.com/photo-1562813733-b31f71025d54?auto=format&fit=crop&w=1200&q=80',
    excerpt:
      'Key market signals, role demand, and skill priorities to align your preparation.',
  },
  {
    title: 'What Recruiters Expect in Fresher Projects',
    image:
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80',
    excerpt:
      'Project depth, documentation quality, and interview storytelling that make profiles stand out.',
  },
  {
    title: 'Cloud + DevOps + AI: The Most In-Demand Combo',
    image:
      'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1200&q=80',
    excerpt:
      'Why recruiters value multi-cloud deployment, CI/CD, and automation-first thinking.',
  },
  {
    title: 'Medical Coding Career Growth in 2026',
    image:
      'https://images.unsplash.com/photo-1579684385127-1ef15d508118?auto=format&fit=crop&w=1200&q=80',
    excerpt:
      'How CPC/CCS + AI-assisted workflows are changing coding, billing, and healthcare operations.',
  },
  {
    title: 'UI/UX Portfolio Mistakes to Avoid',
    image:
      'https://images.unsplash.com/photo-1559028012-481c04fa702d?auto=format&fit=crop&w=1200&q=80',
    excerpt:
      'Common portfolio gaps that reduce interview conversion and how to fix them fast.',
  },
  {
    title: 'Digital Marketing with AI: Skill Stack for Growth',
    image:
      'https://images.pexels.com/photos/905163/pexels-photo-905163.jpeg?auto=compress&cs=tinysrgb&w=1200',
    excerpt:
      'From campaign automation to analytics storytelling, build a future-ready marketing profile.',
  },
];

const BLOG_READ_MORE = {
  'How to Build an AI Career Roadmap in 120 Days': [
    'Phase 1: Build strong foundation in Python, SQL, and data thinking.',
    'Phase 2: Deliver 2 portfolio projects with measurable outcomes.',
    'Phase 3: Practice interview rounds with mentor feedback loops.',
  ],
  'Top Hiring Trends in Cloud, Data, and Cyber Security': [
    'Recruiters prioritize practical implementation over theory-only profiles.',
    'Hybrid skill sets (cloud + automation + AI) are becoming standard.',
    'Project storytelling and communication quality strongly affect selection.',
  ],
  'What Recruiters Expect in Fresher Projects': [
    'Clear business problem statement and scope definition.',
    'Clean Git history, documentation, and presentation-ready demo.',
    'Ability to explain trade-offs and future improvements confidently.',
  ],
  'Cloud + DevOps + AI: The Most In-Demand Combo': [
    'Modern hiring prefers deployment-ready engineers, not theory-only profiles.',
    'Hands-on CI/CD and observability skills improve shortlisting rates significantly.',
    'Adding AI-assisted operations and automation gives strong role differentiation.',
  ],
  'Medical Coding Career Growth in 2026': [
    'Healthcare providers are increasing demand for accurate coding and compliance.',
    'AI tools speed documentation review, but domain understanding remains critical.',
    'Candidates with CPC/CCS plus billing awareness receive better role opportunities.',
  ],
  'UI/UX Portfolio Mistakes to Avoid': [
    'Show process and reasoning, not only final screens.',
    'Use measurable outcomes from case studies to prove impact.',
    'Keep UX writing, accessibility, and responsive design evidence in every project.',
  ],
  'Digital Marketing with AI: Skill Stack for Growth': [
    'Recruiters now expect campaign automation and data-backed optimization decisions.',
    'Performance marketers with AI tool fluency are getting faster career progression.',
    'Build a portfolio with SEO, paid ads, and analytics dashboards for credibility.',
  ],
};

const BOT_OPTIONS = ['Courses', 'Placements', 'Fees', 'Batches', 'Demo Class', 'Contact'];
const BOT_RESPONSES = {
  Placements:
    'We provide placement support with resume review, mock interviews, aptitude prep, and referral guidance.',
  Fees:
    'Fees depend on course and learning mode. Select a course below and click Book a Demo to get complete details.',
  Batches:
    'We run weekday and weekend batches in online, offline, and hybrid modes.',
  'Demo Class':
    'Great choice. Please choose your course below and click Book a Demo to reserve your slot.',
  Contact:
    `You can call +91 91212 86043 or WhatsApp ${WHATSAPP_DISPLAY} for instant admissions support.`,
};

const QUESTION_BANK = {
  'AI & ML': [
    ['What is overfitting?', ['Model too simple', 'Model memorizes training data', 'No training data', 'Wrong optimizer'], 'Model memorizes training data', 'Medium'],
    ['Which metric is common for classification?', ['RMSE', 'Accuracy', 'MAE', 'MAPE'], 'Accuracy', 'Easy'],
    ['What helps prevent overfitting?', ['More epochs only', 'Regularization', 'No validation', 'Random labels'], 'Regularization', 'Hard'],
  ],
  'Python With AI': [
    ['Which keyword defines a function in Python?', ['func', 'def', 'lambda', 'method'], 'def', 'Easy'],
    ['Which library is widely used for ML in Python?', ['scikit-learn', 'pygame', 'tkinter', 'flask-login'], 'scikit-learn', 'Medium'],
    ['List comprehension creates?', ['Loop object', 'List quickly', 'Tuple only', 'None'], 'List quickly', 'Easy'],
  ],
  'Data Science with AI': [
    ['EDA stands for?', ['Exploratory Data Analysis', 'External Data Access', 'Error Data Alignment', 'Extra Deep Architecture'], 'Exploratory Data Analysis', 'Easy'],
    ['Which algorithm is supervised?', ['KMeans', 'Linear Regression', 'Apriori', 'PCA'], 'Linear Regression', 'Medium'],
    ['Feature scaling helps with?', ['Only storage', 'Model convergence', 'Data deletion', 'Query speed'], 'Model convergence', 'Hard'],
  ],
  'Data Analytics with AI': [
    ['Which tool is common for dashboards?', ['Power BI', 'Git', 'Docker', 'Postman'], 'Power BI', 'Easy'],
    ['SQL GROUP BY is used for?', ['Sorting files', 'Aggregation', 'Deployment', 'Styling'], 'Aggregation', 'Medium'],
    ['KPI means?', ['Key Performance Indicator', 'Known Program Input', 'Kernel Process Info', 'Key Product Index'], 'Key Performance Indicator', 'Easy'],
  ],
  'Full Stack Developer (Java, Python)': [
    ['HTTP method usually used to create resource?', ['GET', 'POST', 'DELETE', 'OPTIONS'], 'POST', 'Easy'],
    ['JWT commonly used for?', ['Styling pages', 'Authentication', 'Image resize', 'SQL joins'], 'Authentication', 'Medium'],
    ['REST API should be?', ['Stateful always', 'Stateless', 'Binary only', 'UI-only'], 'Stateless', 'Hard'],
  ],
  '.NET Developer': [
    ['C# runs mainly on?', ['JVM', '.NET', 'Node', 'Ruby VM'], '.NET', 'Easy'],
    ['LINQ is used for?', ['Image editing', 'Querying data', 'Cloud billing', 'UI theming'], 'Querying data', 'Medium'],
    ['ASP.NET Core used for?', ['Desktop wallpaper', 'Web APIs/apps', 'Spreadsheet calc', 'Printer drivers'], 'Web APIs/apps', 'Medium'],
  ],
  'Cyber Security with AI': [
    ['CIA triad includes?', ['Confidentiality, Integrity, Availability', 'Code, Integration, Access', 'Cloud, Identity, Audit', 'Core, Input, API'], 'Confidentiality, Integrity, Availability', 'Easy'],
    ['Phishing is?', ['Hardware failure', 'Social engineering attack', 'Database index', 'Code formatter'], 'Social engineering attack', 'Easy'],
    ['SIEM tools are used for?', ['Video editing', 'Log monitoring and security alerts', 'DNS hosting', 'UI prototyping'], 'Log monitoring and security alerts', 'Medium'],
  ],
  'Cloud Computing (AWS, Azure, GCP) with DevOps & AI': [
    ['Docker is mainly for?', ['Containerization', 'Database backup only', 'Image rendering', 'Typing speed'], 'Containerization', 'Easy'],
    ['CI/CD pipeline purpose?', ['Manual-only release', 'Automate build/test/deploy', 'Disable testing', 'Replace code review'], 'Automate build/test/deploy', 'Medium'],
    ['Kubernetes manages?', ['Email inbox', 'Container orchestration', 'Text editor', 'CDN analytics'], 'Container orchestration', 'Hard'],
  ],
  'UI/UX Design': [
    ['Wireframe is?', ['Final polished UI', 'Low-fidelity layout', 'Database schema', 'API response'], 'Low-fidelity layout', 'Easy'],
    ['UX research helps to?', ['Reduce fonts', 'Understand user needs', 'Compile backend', 'Encrypt passwords'], 'Understand user needs', 'Medium'],
    ['Design system provides?', ['Random colors', 'Consistency and reusable components', 'Server memory', 'SQL views'], 'Consistency and reusable components', 'Hard'],
  ],
  'Digital Marketing': [
    ['SEO stands for?', ['Search Engine Optimization', 'Secure Email Ops', 'System Error Output', 'Search Engagement Ops'], 'Search Engine Optimization', 'Easy'],
    ['CTR means?', ['Click Through Rate', 'Campaign Tracking Rule', 'Cost to Run', 'Client Test Result'], 'Click Through Rate', 'Easy'],
    ['A/B testing is for?', ['Database sharding', 'Comparing variants for performance', 'Cloud billing', 'Compiler speed'], 'Comparing variants for performance', 'Medium'],
  ],
  'Medical Coding with AI (CPC, CCS), IP-DRG, Surgery, E/M, OP, HCC': [
    ['ICD codes are for?', ['Diagnosis classification', 'Salary calculation', 'Ad campaign', 'UI grids'], 'Diagnosis classification', 'Easy'],
    ['CPT codes mainly represent?', ['Procedures/services', 'Server logs', 'Student IDs', 'OS configs'], 'Procedures/services', 'Medium'],
    ['HCC generally relates to?', ['Risk adjustment coding', 'Web hosting', 'SEO ranking', 'Cloud compute units'], 'Risk adjustment coding', 'Hard'],
  ],
  'Medical Billing & RCM with AI': [
    ['RCM stands for?', ['Revenue Cycle Management', 'Record Code Mapping', 'Remote Clinic Module', 'Rate Card Metric'], 'Revenue Cycle Management', 'Easy'],
    ['Claim denial management focuses on?', ['Ad design', 'Resolving rejected claims', 'Docker upgrades', 'Model tuning'], 'Resolving rejected claims', 'Medium'],
    ['AR in billing commonly means?', ['Accounts Receivable', 'Annual Report', 'Access Role', 'Archive Record'], 'Accounts Receivable', 'Easy'],
  ],
};

const buildQuestionSet = (topic, minimum = 100) => {
  const source = QUESTION_BANK[topic] || [];
  if (!source.length) return [];
  return Array.from({ length: minimum }, (_, idx) => {
    const seed = source[idx % source.length];
    const [question, options, answer] = seed;
    return {
      id: `${topic}-${idx + 1}`,
      question: `${question} (Practice ${idx + 1})`,
      options: [...options],
      answer,
    };
  });
};

const COURSE_QUESTION_SETS = Object.fromEntries(
  COURSES.map((course) => [course.title, buildQuestionSet(course.title, 100)])
);

const toQuestions = (topic) => COURSE_QUESTION_SETS[topic] || [];

const BATCH_SCHEDULE = [
  { mode: 'Weekday', format: 'Offline - Ameerpet', timing: 'Morning Batch', mentor: 'Industry Expert Trainer' },
  { mode: 'Weekday', format: 'Online Live', timing: 'Evening Batch', mentor: 'Industry Expert Trainer' },
  { mode: 'Weekend', format: 'Offline - Ameerpet', timing: 'Morning Batch', mentor: 'Industry Expert Trainer' },
  { mode: 'Weekend', format: 'Online Live', timing: 'Evening Batch', mentor: 'Industry Expert Trainer' },
];

const PLACEMENT_PROOF = [
  {
    name: 'Praneeth',
    role: 'Data Analyst',
    timeline: '5 months',
    image: 'https://images.pexels.com/photos/1681010/pexels-photo-1681010.jpeg?auto=compress&cs=tinysrgb&w=600',
    highlight: 'From graduate to first analyst role with project portfolio support.',
  },
  {
    name: 'Harini',
    role: 'Python Developer',
    timeline: '4.5 months',
    image: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=600',
    highlight: 'Cleared 4 interviews after coding practice and mock interview bootcamp.',
  },
  {
    name: 'Karthik',
    role: 'Cloud Support Engineer',
    timeline: '6 months',
    image: 'https://images.pexels.com/photos/91227/pexels-photo-91227.jpeg?auto=compress&cs=tinysrgb&w=600',
    highlight: 'Shifted from non-IT background to cloud role through guided roadmap.',
  },
];

const TRACK_LANDING = [
  {
    slug: 'ai-data',
    title: 'AI & Data Career Track',
    summary: 'For AI, ML, Data Science, and Analytics aspirants.',
    outcomes: ['Data Analyst', 'ML Engineer', 'AI Associate'],
  },
  {
    slug: 'fullstack-software',
    title: 'Software Engineering Track',
    summary: 'For Full Stack, Python, and .NET development careers.',
    outcomes: ['Full Stack Developer', 'Backend Developer', '.NET Developer'],
  },
  {
    slug: 'medical-healthcare',
    title: 'Medical Coding & Billing Track',
    summary: 'For medical coding, RCM, and healthcare operations roles.',
    outcomes: ['Medical Coder', 'RCM Executive', 'Claims Analyst'],
  },
];

const FAQS = [
  {
    q: 'Do you provide placement support?',
    a: 'Yes. We provide resume support, mock interviews, aptitude and HR preparation, and interview referral guidance.',
  },
  {
    q: 'Can I attend classes in online and offline mode?',
    a: 'Yes. We support online, offline, and hybrid batches so you can choose a schedule that fits your routine.',
  },
  {
    q: 'How soon can I start?',
    a: 'New weekday and weekend batches begin frequently. Submit the form and our team will call with nearest start date.',
  },
  {
    q: 'Do you provide demo class?',
    a: 'Yes. You can book a free demo class from the website and our admissions team will arrange it.',
  },
];

const HIRING_PARTNERS = [
  {
    name: 'TCS',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b1/Tata_Consultancy_Services_Logo.svg/512px-Tata_Consultancy_Services_Logo.svg.png',
    fallbackLogo: 'https://logo.clearbit.com/tcs.com',
  },
  { name: 'Infosys', logo: 'https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg' },
  { name: 'Accenture', logo: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Accenture.svg' },
  { name: 'Wipro', logo: 'https://upload.wikimedia.org/wikipedia/commons/a/a0/Wipro_Primary_Logo_Color_RGB.svg' },
  { name: 'Cognizant', logo: 'https://upload.wikimedia.org/wikipedia/commons/4/43/Cognizant_logo_2022.svg' },
  {
    name: 'Tech Mahindra',
    logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/2/24/Tech_Mahindra_New_Logo.svg/512px-Tech_Mahindra_New_Logo.svg.png',
    fallbackLogo: 'https://logo.clearbit.com/techmahindra.com',
  },
];

const SALARY_RANGES = [
  { track: 'AI / Data Tracks', range: '3.5 LPA - 8.0 LPA' },
  { track: 'Full Stack / .NET', range: '3.0 LPA - 7.5 LPA' },
  { track: 'Cloud / DevOps / Security', range: '4.0 LPA - 9.0 LPA' },
  { track: 'UI/UX / Digital Marketing', range: '2.8 LPA - 6.5 LPA' },
  { track: 'Medical Coding / Billing', range: '2.5 LPA - 5.5 LPA' },
];

function CountUpOnView({ end, duration = 1400, prefix = '', suffix = '', decimals = 0 }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || hasAnimated) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        setHasAnimated(true);

        const start = performance.now();
        const animate = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const next = end * progress;
          setValue(next);
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [duration, end, hasAnimated]);

  return (
    <span ref={ref}>
      {prefix}
      {value.toFixed(decimals)}
      {suffix}
    </span>
  );
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef(null);
  const links = [
    ['/', 'Home'],
    ['/courses', 'Courses'],
    ['/interview-prep', 'Interview Prep'],
    ['/blog', 'Blog'],
    ['/contact', 'Contact'],
  ];

  useEffect(() => {
    const onDocClick = (event) => {
      if (!menuOpen) return;
      if (!headerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    const onEscape = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [menuOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header ref={headerRef} className={scrolled ? 'cc-header glass scrolled' : 'cc-header glass'}>
      <div className="cc-brand">
        <img src="/career-campus-logo.png" alt="Career Campus logo" />
        <div>
          <strong>CAREER CAMPUS</strong>
          <span>Where Skills Meet Success</span>
        </div>
      </div>
      <button
        className="menu-toggle"
        type="button"
        aria-label="Toggle navigation menu"
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
      >
        <span className={menuOpen ? 'hamburger open' : 'hamburger'} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      <nav className={menuOpen ? 'cc-nav-links open' : 'cc-nav-links'}>
        {links.map(([to, label]) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) => (isActive ? 'active' : '')}
            onClick={() => setMenuOpen(false)}
          >
            {label}
          </NavLink>
        ))}
        <NavLink to="/contact?intent=demo" className="demo-link" onClick={() => setMenuOpen(false)}>
          Book Free Demo
        </NavLink>
      </nav>
    </header>
  );
}

function HomePage() {
  const [expanded, setExpanded] = useState({});
  const [serviceExpanded, setServiceExpanded] = useState({});
  const featured = COURSES.slice(0, 6);
  return (
    <section className="cc-page">
      <div className="hero-grid">
        <motion.div initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }}>
          <p className="kicker text-slide">Career-Focused Learning Platform</p>
          <h1 className="text-bounce">Transform Your Future with Industry-Ready Training Programs.</h1>
          <p className="text-fade-loop">
            Career Campus helps students and professionals become job-ready with mentor-led
            training, practical projects, interview preparation, and placement-focused guidance.
          </p>
          <div className="cta-row">
            <NavLink className="btn-primary" to="/courses">
              Explore Programs
            </NavLink>
            <NavLink className="btn-outline" to="/contact">
              Get Started
            </NavLink>
            <NavLink className="btn-outline" to="/contact?intent=demo">
              Book Free Demo Class
            </NavLink>
          </div>
          <div className="trust-row">
            <span>Trusted Learning Outcomes</span>
            <div>
              <b>
                <CountUpOnView end={10000} suffix="+" /> Learners
              </b>
              <b>
                <CountUpOnView end={2500} suffix="+" /> Interview Calls
              </b>
              <b>
                <CountUpOnView end={95} suffix="%" /> Completion
              </b>
            </div>
          </div>
        </motion.div>
        <motion.div className="hero-media" initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}>
          <img
            src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1400&q=80"
            alt="students learning"
          />
          <div className="hero-float-card">
            <h4>Admissions Open</h4>
            <p>Online, Offline & Hybrid batches available.</p>
          </div>
        </motion.div>
      </div>

      <div className="stat-grid">
        <article className="panel">
          <strong>
            <CountUpOnView end={120} suffix="+" />
          </strong>
          <span>Live Classes / Month</span>
        </article>
        <article className="panel">
          <strong>
            <CountUpOnView end={40} suffix="+" />
          </strong>
          <span>Expert Trainers</span>
        </article>
        <article className="panel">
          <strong>
            <CountUpOnView end={4.8} suffix="/5" decimals={1} />
          </strong>
          <span>Learner Rating</span>
        </article>
        <article className="panel">
          <strong>
            <CountUpOnView end={85} suffix="%+" />
          </strong>
          <span>Career Benefit Reported</span>
        </article>
      </div>

      <section>
        <h2 className="section-title-color">Featured Programs</h2>
        <div className="cards-grid">
          {featured.map((course) => (
            <article key={course.title} className="panel animated-card">
              <img src={course.image} alt={course.title} className="card-image" />
              <h3>{COURSE_CARD_TITLES[course.title] || course.title}</h3>
              <p>
                <b>{course.category}</b>
              </p>
              <p>{COURSE_CARD_SUBTITLES[course.title]}</p>
              <p>{course.details.slice(0, 88)}...</p>
              {expanded[course.title] ? (
                <div className="readmore-box">
                  <p>{course.details}</p>
                  <p>
                    <b>Key Modules:</b> {COURSE_READ_MORE[course.title]?.curriculum.join(' | ')}
                  </p>
                  <p>
                    <b>Tools:</b> {COURSE_READ_MORE[course.title]?.tools.join(', ')}
                  </p>
                  <p>
                    <b>Career Roles:</b> {COURSE_READ_MORE[course.title]?.roles.join(', ')}
                  </p>
                </div>
              ) : null}
              <button
                type="button"
                className="link-btn"
                onClick={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [course.title]: !prev[course.title],
                  }))
                }
              >
                {expanded[course.title] ? 'Read Less' : 'Read More'}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section>
        <article className="panel promise-strip">
          <h3>Talk To Admissions In 10 Minutes</h3>
          <p>
            Submit your enquiry and our counselor will call you with fees, syllabus, batch timing,
            and career roadmap guidance.
          </p>
          <NavLink className="btn-primary" to="/contact?intent=callback">
            Request Callback
          </NavLink>
        </article>
      </section>

      <section>
        <article className="panel webinar-banner">
          <h3>Upcoming Live Career Webinar</h3>
          <p>
            Join our live webinar on interview strategy, project portfolio, and placement roadmap.
          </p>
          <NavLink className="btn-primary" to="/contact?intent=webinar">
            Register Webinar
          </NavLink>
        </article>
      </section>

      <section>
        <h2 className="section-title-color">Why Students Choose Career Campus</h2>
        <div className="cards-grid">
          {SERVICE_HIGHLIGHTS.map((item) => (
            <article key={item.title} className="panel animated-card">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <div className={serviceExpanded[item.title] ? 'service-more open' : 'service-more'}>
                <p>{item.more}</p>
              </div>
              <button
                className="link-btn"
                type="button"
                onClick={() =>
                  setServiceExpanded((prev) => ({
                    ...prev,
                    [item.title]: !prev[item.title],
                  }))
                }
              >
                {serviceExpanded[item.title] ? 'Show Less' : 'Learn More'}
              </button>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="section-title-color">Batch Schedule Widget</h2>
        <div className="cards-grid">
          {BATCH_SCHEDULE.map((batch) => (
            <article key={`${batch.mode}-${batch.format}`} className="panel batch-card">
              <h3>{batch.mode}</h3>
              <p>{batch.format}</p>
              <p>
                <b>Timing:</b> {batch.timing}
              </p>
              <p>
                <b>Trainer:</b> {batch.mentor}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="section-title-color">Why Ameerpet Learners Choose Us</h2>
        <article className="panel">
          <p>
            Located in the heart of Ameerpet, Career Campus is easy to access from metro and bus
            routes, helping students save commute time and stay consistent with training.
          </p>
          <p>
            Learners trust us for practical teaching, friendly mentor support, and strong local
            placement preparation aligned to Hyderabad hiring expectations.
          </p>
        </article>
      </section>

      <section>
        <h2 className="section-title-color">Placement Proof Highlights</h2>
        <div className="cards-grid">
          {PLACEMENT_PROOF.map((item) => (
            <article key={item.name} className="panel">
              <img src={item.image} alt={item.name} className="review-avatar" />
              <h3>{item.name}</h3>
              <p>{item.role}</p>
              <p>
                <b>Placement Timeline:</b> {item.timeline}
              </p>
              <p>{item.highlight}</p>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="section-title-color">Choose Your Career Landing Page</h2>
        <div className="cards-grid">
          {TRACK_LANDING.map((track) => (
            <article key={track.slug} className="panel">
              <h3>{track.title}</h3>
              <p>{track.summary}</p>
              <p>
                <b>Top Outcomes:</b> {track.outcomes.join(', ')}
              </p>
              <NavLink to={`/track/${track.slug}`}>Open Landing Page</NavLink>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="section-title-color">Placement Salary Range</h2>
        <div className="cards-grid">
          {SALARY_RANGES.map((item) => (
            <article key={item.track} className="panel">
              <h3>{item.track}</h3>
              <p>
                <b>Expected Range:</b> {item.range}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="section-title-color">Hiring Partners</h2>
        <div className="hiring-strip">
          {HIRING_PARTNERS.map((partner) => (
            <article key={partner.name} className="partner-logo">
              <img
                src={partner.logo}
                alt={partner.name}
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  if (partner.fallbackLogo && event.currentTarget.src !== partner.fallbackLogo) {
                    event.currentTarget.src = partner.fallbackLogo;
                    return;
                  }
                  event.currentTarget.src = `https://placehold.co/160x48/ffffff/0f172a?text=${encodeURIComponent(
                    partner.name
                  )}`;
                }}
              />
              <span>{partner.name}</span>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h2 className="section-title-color">What Our Learners Say</h2>
        <div className="cards-grid">
          {REVIEWS.map((r) => (
            <article key={r.name} className="panel animated-card">
              <img src={r.image} alt={r.name} className="review-avatar" />
              <p className="stars">★★★★★</p>
              <p>{r.quote}</p>
              <h4>{r.name}</h4>
              <span>{r.role}</span>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function CoursesPage() {
  const [filter, setFilter] = useState('All');
  const [expanded, setExpanded] = useState({});
  const categories = useMemo(() => ['All', ...new Set(COURSES.map((c) => c.category))], []);
  const list =
    filter === 'All' ? COURSES : COURSES.filter((course) => course.category === filter);

  return (
    <section className="cc-page">
      <h1>All Courses We Offer</h1>
      <div className="chip-row">
        {categories.map((cat) => (
          <button
            key={cat}
            className={cat === filter ? 'chip active' : 'chip'}
            type="button"
            onClick={() => setFilter(cat)}
          >
            {cat}
          </button>
        ))}
      </div>
      <div className="cards-grid">
        {list.map((course) => (
          <article key={course.title} className="panel">
            <img src={course.image} alt={course.title} className="card-image" loading="lazy" />
            <h3>{COURSE_CARD_TITLES[course.title] || course.title}</h3>
            <p>
              <b>{course.category}</b>
            </p>
            <p>{COURSE_CARD_SUBTITLES[course.title]}</p>
            <p>{course.details.slice(0, 95)}...</p>
            {expanded[course.title] ? (
              <div className="readmore-box">
                <p>{course.details}</p>
                <p>
                  <b>Curriculum:</b> {COURSE_READ_MORE[course.title]?.curriculum.join(' | ')}
                </p>
                <p>
                  <b>Tools:</b> {COURSE_READ_MORE[course.title]?.tools.join(', ')}
                </p>
                <p>
                  <b>Career Outcomes:</b> {COURSE_READ_MORE[course.title]?.roles.join(', ')}
                </p>
              </div>
            ) : null}
            <div className="cta-row">
              <button
                type="button"
                className="btn-outline"
                onClick={() =>
                  setExpanded((prev) => ({
                    ...prev,
                    [course.title]: !prev[course.title],
                  }))
                }
              >
                {expanded[course.title] ? 'Read Less' : 'Read More'}
              </button>
              <NavLink className="btn-primary" to="/contact">
                Enroll Now
              </NavLink>
              <button
                type="button"
                className="btn-outline"
                onClick={() => downloadSyllabusPdf(course)}
              >
                Download Syllabus PDF
              </button>
              <a
                className="btn-outline"
                href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
                  `Hi Career Campus, please share fee and syllabus for ${course.title}.`
                )}`}
                target="_blank"
                rel="noreferrer"
              >
                Ask Fee in WhatsApp
              </a>
            </div>
          </article>
        ))}
      </div>

      <section>
        <h2 className="section-title-color">Career Outcomes By Track</h2>
        <div className="cards-grid">
          {list.slice(0, 6).map((course) => (
            <article key={`${course.title}-outcome`} className="panel">
              <h3>{course.title}</h3>
              <p>
                <b>Top Roles:</b> {COURSE_READ_MORE[course.title]?.roles.join(', ')}
              </p>
              <p>
                <b>Tools:</b> {COURSE_READ_MORE[course.title]?.tools.join(', ')}
              </p>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function InterviewPrepPage() {
  const [topic, setTopic] = useState('AI & ML');
  const paper = useMemo(() => toQuestions(topic), [topic]);

  return (
    <section className="cc-page exam-bg">
      <h1>Interview Prep - 100 Questions Per Course</h1>
      <div className="test-toolbar">
        <select value={topic} onChange={(e) => setTopic(e.target.value)}>
          {COURSES.map((course) => (
            <option key={course.title}>{course.title}</option>
          ))}
        </select>
      </div>
      <article className="panel">
        <h3>{topic}</h3>
        <p>
          Total Questions: <b>{paper.length}</b>
        </p>
      </article>
      {paper.map((q, idx) => (
        <article key={q.id} className="panel">
          <p>
            <b>
              Q{idx + 1}. {q.question}
            </b>
          </p>
          {q.options.map((opt) => (
            <p key={opt}>- {opt}</p>
          ))}
        </article>
      ))}
      {!paper.length ? (
        <article className="panel">
          <p>No questions available for this course yet.</p>
          <p>Please select another course from the dropdown.</p>
        </article>
      ) : null}
    </section>
  );
}

function BlogPage() {
  const [expanded, setExpanded] = useState({});
  return (
    <section className="cc-page">
      <h1>Career Insights & Learning Tips</h1>
      <div className="cards-grid">
        {BLOGS.map((blog) => (
          <article className="panel" key={blog.title}>
            <img src={blog.image} alt={blog.title} className="card-image" />
            <h3>{blog.title}</h3>
            <p>{blog.excerpt}</p>
            {expanded[blog.title] ? (
              <ul className="deep-list">
                {BLOG_READ_MORE[blog.title].map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : null}
            <button
              className="link-btn"
              type="button"
              onClick={() =>
                setExpanded((prev) => ({
                  ...prev,
                  [blog.title]: !prev[blog.title],
                }))
              }
            >
              {expanded[blog.title] ? 'Read Less' : 'Read More'}
            </button>
          </article>
        ))}
      </div>
    </section>
  );
}

function ContactPage() {
  const [step, setStep] = useState(1);
  const [faqOpen, setFaqOpen] = useState({});
  const queryParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const intent = queryParams.get('intent') || 'general';
  const utmSource = queryParams.get('utm_source') || '';
  const utmCampaign = queryParams.get('utm_campaign') || '';
  const preselectedCourse = queryParams.get('course') || '';
  const [selectedCourse, setSelectedCourse] = useState(preselectedCourse);

  return (
    <section className="cc-page">
      <h1>Book a Demo / Contact Us - Get a Call Back</h1>
      <p>
        Fill this form to get course details, fee structure, batch timings, and a call from our
        admissions team.
      </p>
      <form className="panel form-grid colorful-form" action={`https://formsubmit.co/${CONTACT_EMAIL}`} method="POST">
        <input type="hidden" name="_subject" value="New Student Registration - Career Campus" />
        <input type="hidden" name="_captcha" value="false" />
        <input type="hidden" name="_template" value="table" />
        <input type="hidden" name="_next" value="https://careercampustech.in/thank-you" />
        <input type="hidden" name="Enquiry Intent" value={intent} />
        <input type="hidden" name="utm_source" value={utmSource} />
        <input type="hidden" name="utm_campaign" value={utmCampaign} />
        {step === 1 ? (
          <>
            <div className="grid-2">
              <input name="First Name" placeholder="First Name" required />
              <input name="Last Name" placeholder="Last Name" />
            </div>
            <input type="email" name="Email" placeholder="Email Address" required />
            <input name="Phone Number" placeholder="Phone Number" required />
            <button className="btn-primary" type="button" onClick={() => setStep(2)}>
              Next Step
            </button>
          </>
        ) : (
          <>
            <select
              name="Interested Course"
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              required
            >
              <option value="" disabled>
                Select Interested Course
              </option>
              {COURSES.map((course) => (
                <option key={course.title} value={course.title}>
                  {course.title}
                </option>
              ))}
            </select>
            <textarea
              name="Message"
              rows={5}
              placeholder="Tell us your background and goals"
              required
            />
            <div className="cta-row">
              <button className="btn-outline" type="button" onClick={() => setStep(1)}>
                Back
              </button>
              <button className="btn-primary" type="submit">
                Book Demo / Get Call Back
              </button>
            </div>
          </>
        )}
        <p className="status">Form sends directly to {CONTACT_EMAIL} via FormSubmit.</p>
      </form>
      <article className="panel contact-card">
        <h3>Contact</h3>
        <p>
          Phone: <a href={`tel:${PHONE}`}>{PHONE}</a>
        </p>
        <p>WhatsApp: {WHATSAPP_DISPLAY}</p>
        <p>Email: {CONTACT_EMAIL}</p>
      </article>

      <article className="panel">
        <h3>Frequently Asked Questions</h3>
        {FAQS.map((item) => (
          <div key={item.q} className="faq-item">
            <button
              type="button"
              className="faq-btn"
              onClick={() =>
                setFaqOpen((prev) => ({
                  ...prev,
                  [item.q]: !prev[item.q],
                }))
              }
            >
              {item.q}
            </button>
            {faqOpen[item.q] ? <p>{item.a}</p> : null}
          </div>
        ))}
      </article>
    </section>
  );
}

function ThankYouPage() {
  useEffect(() => {
    const id = setTimeout(() => {
      window.location.href = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
        'Hi Career Campus, I submitted the enquiry form. Please share next steps.'
      )}`;
    }, 5000);
    return () => clearTimeout(id);
  }, []);

  return (
    <section className="cc-page">
      <article className="panel">
        <h1>Thank You!</h1>
        <p>Your enquiry has been submitted successfully.</p>
        <p>Our admissions team will contact you shortly.</p>
        <p>You will be redirected to WhatsApp in 5 seconds for faster follow-up.</p>
        <NavLink className="btn-primary" to="/">
          Back to Home
        </NavLink>
      </article>
    </section>
  );
}

function TrackLandingPage() {
  const { slug } = useParams();
  const track = TRACK_LANDING.find((item) => item.slug === slug);

  if (!track) {
    return (
      <section className="cc-page">
        <article className="panel">
          <h1>Track Not Found</h1>
          <NavLink to="/">Back to Home</NavLink>
        </article>
      </section>
    );
  }

  return (
    <section className="cc-page">
      <h1>{track.title}</h1>
      <article className="panel">
        <p>{track.summary}</p>
        <p>
          <b>Career Outcomes:</b> {track.outcomes.join(', ')}
        </p>
        <div className="cta-row">
          <NavLink className="btn-primary" to="/contact?intent=demo">
            Book Free Demo
          </NavLink>
          <a className="btn-outline" href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer">
            Talk on WhatsApp
          </a>
        </div>
      </article>
    </section>
  );
}

function OptionBot() {
  const [open, setOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [showCourseOptions, setShowCourseOptions] = useState(false);
  const endRef = useRef(null);
  const [messages, setMessages] = useState([
    { role: 'bot', text: '🤖 Hey! I am Career Campus Assistant. How can I help you today?' },
  ]);
  const courseInfo = selectedCourse ? COURSES.find((course) => course.title === selectedCourse) : null;

  useEffect(() => {
    const id = setTimeout(() => setOpen(true), 900);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, open, showCourseOptions, selectedCourse]);

  const choose = (opt) => {
    if (opt === 'Courses') {
      setShowCourseOptions(true);
      setMessages((prev) => [...prev, { role: 'user', text: opt }, { role: 'bot', text: 'Please select a course to view details.' }]);
      return;
    }
    setShowCourseOptions(false);
    setMessages((prev) => [...prev, { role: 'user', text: opt }, { role: 'bot', text: BOT_RESPONSES[opt] }]);
  };

  const chooseCourse = (title) => {
    setSelectedCourse(title);
    const detail = COURSE_READ_MORE[title];
    setMessages((prev) => [
      ...prev,
      { role: 'user', text: title },
      {
        role: 'bot',
        text: `${title}: ${COURSES.find((course) => course.title === title)?.details} Top Roles: ${detail?.roles.join(', ') || ''}.`,
      },
    ]);
  };

  return (
    <>
      <button className="chat-toggle" type="button" onClick={() => setOpen((v) => !v)}>
        🤖 Ask Assistant
      </button>
      {open ? (
        <section className="chat-panel">
          <div className="chat-head">
            <h4>🤖 CareerCampus Assistant</h4>
            <button className="chat-close" type="button" onClick={() => setOpen(false)} aria-label="Close assistant">
              ✕
            </button>
          </div>
          <div className="chat-messages">
            {messages.map((m, i) => (
              <p key={`${m.role}-${i}`} className={m.role}>
                {m.role === 'bot' ? '🤖 ' : '🙋 '} {m.text}
              </p>
            ))}
            <div ref={endRef} />
          </div>
          <div className="chip-row">
            {BOT_OPTIONS.map((opt) => (
              <button className="chip" key={opt} type="button" onClick={() => choose(opt)}>
                {opt}
              </button>
            ))}
          </div>
          {showCourseOptions ? (
            <div className="chip-row course-chip-row">
              {COURSES.map((course) => (
                <button className="chip chip-course" key={course.title} type="button" onClick={() => chooseCourse(course.title)}>
                  {course.title}
                </button>
              ))}
            </div>
          ) : null}
          {courseInfo ? (
            <div className="cta-row chat-cta-row">
              <NavLink className="btn-primary" to={`/contact?intent=demo&course=${encodeURIComponent(courseInfo.title)}`} onClick={() => setOpen(false)}>
                Book a Demo
              </NavLink>
              <NavLink className="btn-outline" to="/contact" onClick={() => setOpen(false)}>
                Contact Team
              </NavLink>
            </div>
          ) : null}
        </section>
      ) : null}
    </>
  );
}

function FloatingActions() {
  const waUrl = `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
    'Hi Career Campus, I want course and fee details.'
  )}`;
  return (
    <>
      <a className="call-float" href={`tel:${PHONE}`}>
        Call Now
      </a>
      <a className="wa-float" href={waUrl} target="_blank" rel="noreferrer">
        WhatsApp
      </a>
    </>
  );
}

function MobileStickyBar() {
  return (
    <div className="mobile-sticky-cta">
      <NavLink to="/contact?intent=demo">Book Demo</NavLink>
      <a href={`tel:${PHONE}`}>Call</a>
      <a href={`https://wa.me/${WHATSAPP}`} target="_blank" rel="noreferrer">
        WhatsApp
      </a>
    </div>
  );
}

function AnimatedBackdrop() {
  return (
    <div className="bg-orbs bg-pattern" aria-hidden="true">
      <span />
      <span />
      <span />
    </div>
  );
}

function Footer() {
  return (
    <footer className="cc-footer">
      <div className="footer-block footer-brand">
        <h4>Career Campus</h4>
        <p>Where Skills Meet Success.</p>
        <p>Empowering learners with practical skills, confidence, and career transformation.</p>
      </div>
      <div className="footer-block footer-contact">
        <h5>Contact</h5>
        <p>
          <span>Call:</span> <a href="tel:+919121286043">+91 91212 86043</a>
        </p>
        <p>
          <span>WhatsApp:</span> <a href={`https://wa.me/${WHATSAPP}`}>{WHATSAPP_DISPLAY}</a>
        </p>
        <p>
          <span>Email:</span> <a href="mailto:careercampus.tech1@gmail.com">careercampus.tech1@gmail.com</a>
        </p>
      </div>
      <div className="footer-block footer-address">
        <h5>Address</h5>
        <p>
          2nd Floor, CFQ3+F26 Siddhartha Apartments, Shyam Karan Rd, Divyashakti
          Appartments, Ameerpet, Hyderabad, Telangana 500016
        </p>
      </div>
    </footer>
  );
}

export default function MainApp() {
  const location = useLocation();
  const [exitOpen, setExitOpen] = useState(false);

  useEffect(() => {
    const pageTitles = {
      '/': 'Career Campus | Job-Ready Training in Ameerpet',
      '/courses': 'Courses | Career Campus',
      '/interview-prep': 'Interview Prep | Career Campus',
      '/blog': 'Career Insights | Career Campus',
      '/contact': 'Book Demo / Contact Us | Career Campus',
    };
    document.title = pageTitles[location.pathname] || 'Career Campus';
  }, [location.pathname]);

  useEffect(() => {
    const seen = window.sessionStorage.getItem('cc-exit-shown');
    if (seen) return undefined;
    const isDesktop = window.matchMedia('(min-width: 1000px)').matches;
    if (!isDesktop) return undefined;

    const onExitIntent = (event) => {
      if (event.clientY > 0) return;
      setExitOpen(true);
      window.sessionStorage.setItem('cc-exit-shown', '1');
      document.removeEventListener('mouseout', onExitIntent);
    };

    document.addEventListener('mouseout', onExitIntent);
    return () => document.removeEventListener('mouseout', onExitIntent);
  }, []);

  return (
    <div className="cc-root">
      <AnimatedBackdrop />
      <Header />
      <main className="cc-main">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/interview-prep" element={<InterviewPrepPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/track/:slug" element={<TrackLandingPage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </main>
      <Footer />
      <MobileStickyBar />
      <FloatingActions />
      <OptionBot />
      {exitOpen ? (
        <section className="exit-popup">
          <article className="panel">
            <h3>Before You Leave...</h3>
            <p>Book a free counselling call and get a personalized course roadmap.</p>
            <div className="cta-row">
              <NavLink className="btn-primary" to="/contact?intent=callback" onClick={() => setExitOpen(false)}>
                Book Free Counselling
              </NavLink>
              <button className="btn-outline" type="button" onClick={() => setExitOpen(false)}>
                Maybe Later
              </button>
            </div>
          </article>
        </section>
      ) : null}
    </div>
  );
}
